/**
 * DemoModePanel — overlay controls for demo mode when no topology is selected.
 *
 * Provides topology type selector and agent count slider to generate
 * TopologyInfo client-side for previewing all 6 scene layouts.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import type { TopologyInfo } from "../scenes/SceneLayout.js";
import {
  TOPOLOGY_TYPES,
  generateTopology,
  minAgentsFor,
  type TopologyType,
} from "../../demo/topologies.js";

interface DemoModePanelProps {
  onTopologyChange: (topo: TopologyInfo) => void;
}

export function DemoModePanel({ onTopologyChange }: DemoModePanelProps) {
  const [type, setType] = useState<TopologyType>("star");
  const [agentCount, setAgentCount] = useState(4);

  const effectiveCount = Math.max(minAgentsFor(type), agentCount);

  const topology = useMemo(
    () => generateTopology(type, effectiveCount),
    [type, effectiveCount],
  );

  // Notify parent when topology changes
  useEffect(() => {
    onTopologyChange(topology);
  }, [topology, onTopologyChange]);

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setType(e.target.value as TopologyType);
  }, []);

  const handleCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAgentCount(parseInt(e.target.value, 10));
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 20,
        background: "rgba(20, 18, 28, 0.85)",
        backdropFilter: "blur(8px)",
        borderRadius: 8,
        padding: "12px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        border: "1px solid rgba(255, 255, 255, 0.08)",
        minWidth: 180,
        fontFamily: "var(--font-mono, monospace)",
        fontSize: 12,
        color: "rgba(255, 255, 255, 0.7)",
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(255, 255, 255, 0.4)" }}>
        Demo Mode
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span>Layout</span>
        <select
          value={type}
          onChange={handleTypeChange}
          style={{
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 4,
            padding: "4px 6px",
            color: "rgba(255, 255, 255, 0.9)",
            fontSize: 12,
            fontFamily: "inherit",
          }}
        >
          {TOPOLOGY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span>Agents: {effectiveCount}</span>
        <input
          type="range"
          min={2}
          max={8}
          value={agentCount}
          onChange={handleCountChange}
          style={{
            accentColor: "#a855f7",
            width: "100%",
          }}
        />
      </label>

      <div style={{ fontSize: 10, color: "rgba(255, 255, 255, 0.3)", marginTop: 2 }}>
        Select a topology to exit demo
      </div>
    </div>
  );
}
